import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SkillCodeNameTooltip.css';

const GAP = 6;
const VIEWPORT_MARGIN = 8;

function isCodeTooltipAnchorVisible(rect, viewport) {
  return (
    rect.bottom > viewport.top
    && rect.top < viewport.top + viewport.height
    && rect.right > viewport.left
    && rect.left < viewport.left + viewport.width
  );
}

function SkillCodeNameTooltip({ children, name }) {
  const [anchor, setAnchor] = useState(null);
  const [position, setPosition] = useState(null);
  const closeTimeoutRef = useRef(null);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelClose();
    setAnchor(null);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setAnchor(null);
    }, 90);
  }, [cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  useLayoutEffect(() => {
    if (!anchor) {
      setPosition(null);
      return undefined;
    }
    const updatePosition = () => {
      if (!anchor.isConnected) {
        close();
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const viewport = {
        left: visualViewport?.offsetLeft || 0,
        top: visualViewport?.offsetTop || 0,
        width: visualViewport?.width || window.innerWidth,
        height: visualViewport?.height || window.innerHeight,
      };
      if (!isCodeTooltipAnchorVisible(rect, viewport)) {
        close();
        return;
      }
      setPosition({
        top: rect.bottom + GAP,
        left: Math.min(
          Math.max(rect.left + (rect.width / 2), viewport.left + VIEWPORT_MARGIN),
          viewport.left + viewport.width - VIEWPORT_MARGIN,
        ),
      });
    };
    updatePosition();
    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(anchor);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) close();
    });
    intersectionObserver.observe(anchor);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);
    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [anchor, close]);

  return (
    <>
      <span
        className="skill-code-name-tooltip-anchor"
        data-skill-code-tooltip="true"
        tabIndex={0}
        onMouseEnter={(event) => {
          cancelClose();
          setAnchor(event.currentTarget);
        }}
        onMouseLeave={scheduleClose}
        onFocus={(event) => {
          cancelClose();
          setAnchor(event.currentTarget);
        }}
        onBlur={close}
        onClick={(event) => {
          cancelClose();
          setAnchor((current) => (
            current === event.currentTarget ? null : event.currentTarget
          ));
        }}
      >
        {children}
      </span>
      {anchor && position && createPortal(
        <span
          className="skill-code-name-tooltip"
          style={{ top: position.top, left: position.left }}
          role="tooltip"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {name}
        </span>,
        document.body,
      )}
    </>
  );
}

export default SkillCodeNameTooltip;
export { isCodeTooltipAnchorVisible };
